import React, { useState, useEffect, useRef, useMemo } from 'react';
import PuzzleGrid from './PuzzleGrid';
import { puzzleEngine } from '../game/logic/PuzzleEngine';
import { gameState } from '../game/state/GameState';
import { claimTile, solveTile, releaseTile, extendTileLock } from '../game/actions/TileActions';
import {
    markContributed,
    spendEnergy,
    getEnergyState,
    refundEnergyLocal,
    ENERGY_MAX,
} from '../game/meta/progression';
import {
    ASSIST,
    ASSIST_META,
    ensureDailyAssists,
    getAssistInventory,
    spendAssist,
    grantAssist,
    applyVoidStamp,
    applyErrorDust,
    applyChalkTip,
    applySpareKey,
    onSliceSolvedAssistDrops,
    formatLockCountdown,
} from '../game/meta/atelierAssists';
import { IS_LOCAL_MODE, canUseDebugTools } from '../config/game_config';
import { IconKey } from './AtelierIcons';
import {
    requestPuzzleFullscreen,
    exitPuzzleFullscreen,
    onTelegramViewportChange,
    applyTelegramSafeAreaVars,
    isPhonePuzzleViewport,
    expandPuzzleViewport,
    canTogglePuzzleFullscreen,
    isTelegramFullscreen,
    setPuzzleFullscreenPref,
    getPuzzleFullscreenPref,
    estimatePuzzleStageBudget,
    verifyPuzzleFullscreenHelped,
} from '../config/telegramUi';
import TutorialCoach, {
    GUIDED_BOARD_STEPS,
    guidedHighlightsForStep,
    guidedStepComplete,
    paintGuidedStep,
} from './TutorialCoach';

const CELL_EMPTY = 0;

function emptyGrid(n) {
    return Array(n).fill(0).map(() => Array(n).fill(0));
}

function isValidDraft(draft, n) {
    return (
        Array.isArray(draft) &&
        draft.length === n &&
        draft.every((row) => Array.isArray(row) && row.length === n)
    );
}

function vaultProgress() {
    const tiles = Array.from(gameState.vault.getTiles());
    const total = tiles.length || 1;
    const solved = tiles.filter((t) => t.status === 'SOLVED').length;
    return {
        solved,
        total,
        pct: Math.round((solved / total) * 100),
    };
}

const TileDetailsModal = ({ tile, onClose, onFeedback }) => {
    if (!tile) return null;

    const isOwner = tile.claimedBy === gameState.currentUser?.id;
    const isSubmitting = gameState.isSubmitting;
    const sliceLabel =
        tile.data?.axis != null
            ? `Slice ${String(tile.data.axis).toUpperCase()}=${tile.data.index}`
            : tile.id;

    const draftKey = `vq_draft_${gameState.currentUser?.id}_${tile.id}`;
    const [energy, setEnergy] = useState(() => getEnergyState());
    const [assists, setAssists] = useState(() => ensureDailyAssists());
    const [highlights, setHighlights] = useState([]);
    const [gridEpoch, setGridEpoch] = useState(0);
    const [assistSheet, setAssistSheet] = useState(null); // ASSIST id | null
    const [nowTick, setNowTick] = useState(() => Date.now());
    const [showAssists, setShowAssists] = useState(false);
    const [showMore, setShowMore] = useState(false);
    const [roomyChrome, setRoomyChrome] = useState(false);
    const [immersive, setImmersive] = useState(false);
    const [phoneLayout, setPhoneLayout] = useState(() =>
        typeof window !== 'undefined' ? isPhonePuzzleViewport() : true
    );
    const [fsActive, setFsActive] = useState(() =>
        typeof window !== 'undefined' ? isTelegramFullscreen() : false
    );
    const [canFsToggle, setCanFsToggle] = useState(() =>
        typeof window !== 'undefined' ? canTogglePuzzleFullscreen() : false
    );
    const [coachStep, setCoachStep] = useState(0);
    const [coachDismissed, setCoachDismissed] = useState(false);
    const hadClaimRef = useRef(false);

    const isBootcamp = gameState.isBootcampMode;
    const tutorialMeta = tile.data?.tutorial || null;
    const showGuidedCoach =
        isBootcamp && tutorialMeta?.guided && tile.status === 'CLAIMED' && isOwner && !coachDismissed;
    const guidedStep = showGuidedCoach ? GUIDED_BOARD_STEPS[coachStep] : null;
    const isFocusPlay = tile.status === 'CLAIMED' && isOwner;
    const gridN =
        tile.data?.solutionGrid?.length ||
        tile.data?.clues?.rows?.length ||
        0;

    // Phone = viewport-maximize; desktop = centered card (no fullscreen chase).
    // Immersive chrome: landscape always on phone; portrait when 16×16 (dense reclaim).
    useEffect(() => {
        const mql = window.matchMedia('(orientation: landscape)');
        const update = () => {
            const phone = isPhonePuzzleViewport();
            const h = window.visualViewport?.height ?? window.innerHeight;
            const w = window.visualViewport?.width ?? window.innerWidth;
            const landscape = mql.matches || w > h;
            const n =
                tile.data?.solutionGrid?.length ||
                tile.data?.clues?.rows?.length ||
                0;
            setPhoneLayout(phone);
            // Avoid defaulting unknown size to 16 (would wrongly dense-chrome Workshop).
            setImmersive(phone && (landscape || n >= 16));
            setRoomyChrome(!phone && h >= 640);
            setFsActive(isTelegramFullscreen());
            setCanFsToggle(canTogglePuzzleFullscreen());
        };
        update();
        const onMql = () => update();
        mql.addEventListener?.('change', onMql);
        mql.addListener?.(onMql);
        window.addEventListener('resize', update);
        window.addEventListener('orientationchange', update);
        window.visualViewport?.addEventListener('resize', update);
        const offTg = onTelegramViewportChange(update);
        return () => {
            mql.removeEventListener?.('change', onMql);
            mql.removeListener?.(onMql);
            window.removeEventListener('resize', update);
            window.removeEventListener('orientationchange', update);
            window.visualViewport?.removeEventListener('resize', update);
            offTg?.();
        };
    }, [tile.id, tile.data?.solutionGrid, tile.data?.clues]);

    // Expand viewport; request Telegram fullscreen only when it enlarges the board (or user pref).
    useEffect(() => {
        if (!isFocusPlay) return undefined;
        applyTelegramSafeAreaVars();
        if (!isPhonePuzzleViewport()) return undefined;
        expandPuzzleViewport();
        const dense = immersive || gridN >= 16;
        const before = estimatePuzzleStageBudget({ fullscreen: false, denseChrome: dense });
        requestPuzzleFullscreen({ denseChrome: dense });
        const t1 = window.setTimeout(() => applyTelegramSafeAreaVars(), 80);
        const t2 = window.setTimeout(() => {
            applyTelegramSafeAreaVars();
            if (isTelegramFullscreen() && !getPuzzleFullscreenPref()) {
                verifyPuzzleFullscreenHelped({ beforeBudget: before, denseChrome: dense });
            }
            setFsActive(isTelegramFullscreen());
        }, 400);
        const t3 = window.setTimeout(() => {
            applyTelegramSafeAreaVars();
            setFsActive(isTelegramFullscreen());
        }, 700);
        return () => {
            window.clearTimeout(t1);
            window.clearTimeout(t2);
            window.clearTimeout(t3);
        };
    }, [isFocusPlay, tile.id, gridN, immersive]);

    useEffect(() => {
        return () => {
            // Modal unmount (hub / close): leave fullscreen so lobby isn't awkward.
            exitPuzzleFullscreen();
        };
    }, []);

    useEffect(() => {
        setCoachStep(0);
        setCoachDismissed(false);
        setHighlights([]);
    }, [tile.id]);

    const puzzleData = useMemo(() => {
        try {
            const liveSolution = tile.data?.solutionGrid || null;
            const clues = tile.data?.clues;
            if (liveSolution) {
                return {
                    clues: clues || puzzleEngine.generateClues(liveSolution),
                    solution: liveSolution,
                    hasSolution: true,
                };
            }
            if (clues) {
                const n = clues.rows?.length || 16;
                // Clues-only until claim — never invent a fake solution for validation.
                return {
                    clues,
                    solution: null,
                    hasSolution: false,
                    size: n,
                };
            }
            const mockGrid = emptyGrid(8).map((row, r) =>
                row.map((_, c) => (r === c || r === 7 - c ? 1 : 0))
            );
            return {
                clues: puzzleEngine.generateClues(mockGrid),
                solution: mockGrid,
                hasSolution: true,
            };
        } catch (e) {
            const safeGrid = emptyGrid(8);
            return { clues: { rows: [], cols: [] }, solution: safeGrid, hasSolution: false };
        }
    }, [tile.id, tile.data?.solutionGrid, tile.data?.clues]);

    const gridSize =
        puzzleData.solution?.length ||
        puzzleData.size ||
        puzzleData.clues?.rows?.length ||
        16;

    const [gridState, setGridState] = useState(() => {
        const saved = localStorage.getItem(draftKey);
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                if (isValidDraft(parsed, gridSize)) return parsed;
            } catch (e) {
                /* ignore */
            }
        }
        return emptyGrid(gridSize);
    });

    const guidedSolution = tile.data?.solutionGrid || puzzleData.solution || null;

    // Keep glowing cell hints in sync with the guided step + what the player already painted.
    useEffect(() => {
        if (!showGuidedCoach || !guidedStep || !guidedSolution) {
            return;
        }
        const stepId = guidedStep.id;
        if (stepId === 'draw' || stepId === 'mark') {
            setHighlights(guidedHighlightsForStep(guidedSolution, stepId, gridState));
        } else if (stepId === 'clues' || stepId === 'finish') {
            setHighlights([]);
        }
    }, [showGuidedCoach, coachStep, guidedSolution, gridState, guidedStep]);

    // Auto-advance DRAW → MARK → finish when the player completes each paint step.
    useEffect(() => {
        if (!showGuidedCoach || !guidedStep || !guidedSolution) return;
        const stepId = guidedStep.id;
        if (stepId !== 'draw' && stepId !== 'mark') return;
        if (!guidedStepComplete(guidedSolution, stepId, gridState)) return;
        const t = setTimeout(() => {
            setCoachStep((s) => Math.min(s + 1, GUIDED_BOARD_STEPS.length - 1));
        }, 350);
        return () => clearTimeout(t);
    }, [showGuidedCoach, guidedStep, guidedSolution, gridState]);

    const [localFeedback, setLocalFeedback] = useState(null);
    const [showSolution, setShowSolution] = useState(false);
    const [solveResult, setSolveResult] = useState(null);
    const solvingRef = useRef(false);
    const autoCheckSnapRef = useRef('');

    useEffect(() => {
        if (isOwner && !solveResult) {
            const handler = setTimeout(() => {
                localStorage.setItem(draftKey, JSON.stringify(gridState));
            }, 500);
            return () => clearTimeout(handler);
        }
    }, [gridState, isOwner, draftKey, solveResult]);

    useEffect(() => {
        if (solveResult) return;
        setLocalFeedback(null);
        setShowSolution(false);
        autoCheckSnapRef.current = '';
        hadClaimRef.current = tile.status === 'CLAIMED' && isOwner;
    }, [tile.id, solveResult]);

    // Claim timer tick (mobile-visible countdown)
    useEffect(() => {
        if (tile.status !== 'CLAIMED' || !isOwner || !tile.lockExpiry) return undefined;
        const id = setInterval(() => setNowTick(Date.now()), 250);
        return () => clearInterval(id);
    }, [tile.status, isOwner, tile.lockExpiry, tile.id]);

    const lockClock =
        tile.status === 'CLAIMED' && isOwner && tile.lockExpiry
            ? formatLockCountdown(tile.lockExpiry, nowTick)
            : null;

    const handleFeedback = (msg, isError = false) => {
        setLocalFeedback({ msg, isError });
        if (onFeedback) {
            onFeedback(isError ? { type: 'error', title: msg } : msg);
        }
        if (isError) {
            setTimeout(() => setLocalFeedback(null), 3000);
        }
    };

    // Explain timeout when claim drops while this modal is open
    useEffect(() => {
        const owning = tile.status === 'CLAIMED' && isOwner;
        if (hadClaimRef.current && tile.status === 'OPEN') {
            setLocalFeedback({
                msg: 'Claim timed out · slice reopened · draft kept — reclaim (−1 key) to continue',
                isError: true,
            });
            const t = setTimeout(() => setLocalFeedback(null), 4500);
            hadClaimRef.current = owning;
            return () => clearTimeout(t);
        }
        hadClaimRef.current = owning;
        return undefined;
    }, [tile.status, isOwner, tile.id]);

    const handleClaim = async () => {
        // User gesture — expand Mini App; FS only when it helps (or prior Expand pref).
        expandPuzzleViewport();
        requestPuzzleFullscreen({ denseChrome: immersive || gridN >= 16 });

        const me = gameState.currentUser?.id;
        const held = me ? gameState.vault.findActiveLockForUser(me) : null;
        if (held) {
            gameState.selectTile(held.id);
            handleFeedback('Resume your claimed slice');
            return;
        }

        // Bootcamp practice never spends keys.
        if (!isBootcamp) {
            const e = getEnergyState();
            if (e.keys <= 0) {
                handleFeedback('No energy keys — regen every 90 minutes', true);
                return;
            }
            const spent = spendEnergy();
            const res = await claimTile(tile.id);
            if (!res.success) {
                refundEnergyLocal();
                handleFeedback(`Claim Failed: ${res.error}`, true);
                return;
            }
            markContributed();
            handleFeedback(`Slice claimed · Energy ${spent.keys}/${ENERGY_MAX}`);
            return;
        }

        const res = await claimTile(tile.id);
        if (!res.success) {
            handleFeedback(`Could not open practice: ${res.error}`, true);
            return;
        }
        handleFeedback('Free practice board — no key spent');
    };

    const finishSuccessfulSolve = (before) => {
        localStorage.removeItem(draftKey);
        markContributed();

        let energyState = null;
        if (IS_LOCAL_MODE) {
            energyState = refundEnergyLocal();
        } else {
            energyState = getEnergyState();
        }
        setEnergy(getEnergyState());

        const after = vaultProgress();
        const isFirstSliceToday = before.solved === 0;
        const drops = onSliceSolvedAssistDrops({
            pctAfter: after.pct,
            isFirstSliceToday,
            tier: gameState.mysteryTier,
        });
        setAssists(drops.inventory);

        const rewards = [
            { label: 'Reconstruction', value: `${before.pct}% → ${after.pct}%` },
            { label: 'Slices', value: `${after.solved}/${after.total}` },
            { label: 'Contribution', value: '+1 logged' },
        ];
        if (IS_LOCAL_MODE) {
            rewards.push({
                label: 'Energy restored',
                value: `+1 key (${energyState.keys}/${ENERGY_MAX})`,
            });
        }
        if (drops.granted?.length) {
            rewards.push({
                label:
                    gameState.mysteryTier === 'atelier'
                        ? 'Atelier tools'
                        : 'Workshop tools',
                value: drops.granted
                    .map((id) => ASSIST_META[id]?.name || id)
                    .join(', '),
            });
        }

        setSolveResult({
            sliceLabel,
            pctBefore: before.pct,
            pctAfter: after.pct,
            rewards,
        });
        setHighlights([]);
    };

    const handleSolve = async ({ useSolution = false } = {}) => {
        if (solvingRef.current || gameState.isSubmitting) return;
        const expected = tile.data?.solutionGrid || puzzleData.solution;
        // PEEK is view-only — never submit the peeked solution unless DEBUG
        const submission = useSolution ? expected : gridState;

        if (!submission) {
            handleFeedback('No solution grid available for this slice.', true);
            return;
        }

        if (!useSolution) {
            if (!expected) {
                handleFeedback('Solution still loading — tap Check again in a moment', true);
                return;
            }
            const mockTileData = { data: { solutionGrid: expected } };
            const validation = puzzleEngine.validateSolution(mockTileData, submission);
            if (!validation.valid) {
                const msg =
                    validation.error === 'DIMENSION_MISMATCH'
                        ? 'Board size mismatch — clear draft and retry'
                        : 'Slice incorrect — keep adjusting carved and vacant cells';
                handleFeedback(msg, true);
                return;
            }
        }

        solvingRef.current = true;
        try {
            const before = vaultProgress();
            const res = await solveTile(tile.id, submission);
            if (!res.success) {
                handleFeedback(`Solve Failed: ${res.error}`, true);
                return;
            }
            finishSuccessfulSolve(before);
        } finally {
            solvingRef.current = false;
        }
    };

    const handleDebugSolve = () => handleSolve({ useSolution: true });

    const refreshAssists = () => setAssists(getAssistInventory());

    const handleAssist = (id) => {
        if (tile.status !== 'CLAIMED' || !isOwner) return;
        if (showSolution) {
            handleFeedback('Hide PEEK before using atelier tools', true);
            return;
        }

        if (id === ASSIST.SPARE_KEY) {
            if (!spendAssist(id).ok) {
                handleFeedback('No Spare Keys left', true);
                return;
            }
            const res = applySpareKey();
            refreshAssists();
            setEnergy(getEnergyState());
            if (!res.ok) {
                grantAssist(id, 1);
                refreshAssists();
                handleFeedback('Energy already full', true);
                return;
            }
            handleFeedback(`Spare Key used · Energy ${res.keys}/${ENERGY_MAX}`);
            setAssistSheet(null);
            return;
        }

        if (id === ASSIST.LOCK_OIL) {
            if (!spendAssist(id).ok) {
                handleFeedback('No Lock Oil left', true);
                return;
            }
            refreshAssists();
            setAssistSheet(null);
            (async () => {
                const api = await extendTileLock(tile.id);
                if (!api.success) {
                    grantAssist(id, 1);
                    refreshAssists();
                    handleFeedback(
                        api.error === 'NO_CLAIM'
                            ? 'Lock Oil needs an active claim'
                            : 'Could not extend lock — oil refunded',
                        true
                    );
                    return;
                }
                const next =
                    api.data?.lockExpiry ||
                    gameState.vault.getTile(tile.id)?.lockExpiry;
                if (next) setNowTick(Date.now());
                const left = formatLockCountdown(next || Date.now()).label;
                handleFeedback(`Lock Oil · +5 min · ${left} left`);
            })();
            return;
        }

        if (id === ASSIST.VOID_STAMP) {
            const preview = applyVoidStamp(gridState, puzzleData.clues);
            if (!preview.ok) {
                handleFeedback(
                    preview.error === 'NOTHING_TO_MARK'
                        ? 'No empty 0-clue cells to stamp'
                        : 'Void Stamp failed',
                    true
                );
                return;
            }
            if (!spendAssist(id).ok) {
                handleFeedback('No Void Stamps left', true);
                return;
            }
            setGridState(preview.grid);
            setGridEpoch((n) => n + 1);
            setHighlights([]);
            refreshAssists();
            handleFeedback(`Void Stamp · marked ${preview.marked} cells`);
            setAssistSheet(null);
            return;
        }

        if (id === ASSIST.ERROR_DUST) {
            const expected = tile.data?.solutionGrid || puzzleData.solution;
            const res = applyErrorDust(gridState, puzzleData.clues, expected);
            if (!res.ok) {
                handleFeedback(
                    res.error === 'NO_ERRORS'
                        ? 'No wrong DRAW/MARK cells yet — dust only flags mistakes you already painted'
                        : 'Error Dust could not scan this board',
                    true
                );
                return;
            }
            if (!spendAssist(id).ok) {
                handleFeedback('No Error Dust left', true);
                return;
            }
            setHighlights(res.highlights);
            refreshAssists();
            handleFeedback(
                res.capped
                    ? `Error Dust · showing ${res.highlights.length} of ${res.totalFound} mistakes (max 3 per use)`
                    : `Error Dust · ${res.highlights.length} wrong cell${
                          res.highlights.length === 1 ? '' : 's'
                      } (red)`
            );
            setAssistSheet(null);
            return;
        }

        if (id === ASSIST.CHALK_TIP) {
            const expected = tile.data?.solutionGrid || puzzleData.solution;
            if (!expected) {
                handleFeedback('Tip needs the claim solution — reopen this slice', true);
                return;
            }
            const res = applyChalkTip(gridState, expected);
            if (!res.ok) {
                handleFeedback('No empty cells left for a tip', true);
                return;
            }
            if (!spendAssist(id).ok) {
                handleFeedback('No Chalk Tips left', true);
                return;
            }
            if (res.grid) {
                setGridState(res.grid);
                setGridEpoch((n) => n + 1);
            }
            setHighlights(res.highlights);
            refreshAssists();
            handleFeedback(
                res.action === 'DRAW'
                    ? 'Chalk Tip · applied DRAW (filled) on the glowing cell'
                    : 'Chalk Tip · applied MARK (X) on the glowing cell'
            );
            setAssistSheet(null);
        }
    };

    // When every cell is carved or marked, auto-check — no need to tap Submit
    useEffect(() => {
        if (solveResult || showSolution) return;
        if (tile.status !== 'CLAIMED' || !isOwner) return;
        if (solvingRef.current || gameState.isSubmitting) return;

        const hasUntouched = gridState.some((row) =>
            row.some((cell) => cell === CELL_EMPTY)
        );
        if (hasUntouched) {
            autoCheckSnapRef.current = '';
            return;
        }

        const snap = JSON.stringify(gridState);
        if (snap === autoCheckSnapRef.current) return;
        autoCheckSnapRef.current = snap;

        const expected = tile.data?.solutionGrid || puzzleData.solution;
        if (!expected) {
            handleFeedback('Solution still loading — tap Check slice when ready', true);
            return;
        }

        const mockTileData = { data: { solutionGrid: expected } };
        const validation = puzzleEngine.validateSolution(mockTileData, gridState);
        if (!validation.valid) {
            handleFeedback('Slice incorrect — keep adjusting carved and vacant cells', true);
            return;
        }

        handleFeedback('Slice correct — submitting…');
        handleSolve();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: react to grid fills only
    }, [
        gridState,
        solveResult,
        showSolution,
        tile.status,
        isOwner,
        puzzleData.solution,
        tile.data?.solutionGrid,
    ]);

    const gridFullyFilled = !gridState.some((row) =>
        row.some((cell) => cell === CELL_EMPTY)
    );

    const handleRelease = async () => {
        if (
            !confirm(
                IS_LOCAL_MODE
                    ? 'Give up this slice? Your draft is cleared. (Local: key restored.)'
                    : 'Give up this slice? Your draft is cleared and the key is spent.'
            )
        ) {
            return;
        }
        await releaseTile(tile.id);
        localStorage.removeItem(draftKey);
        if (IS_LOCAL_MODE) {
            refundEnergyLocal();
        }
        onClose();
    };

    try {
        if (solveResult) {
            return (
                <div className="modal-backdrop atelier-modal-backdrop" role="presentation">
                    <div className="modal-content atelier-puzzle-modal solve-success-modal">
                        <div className="solve-success-banner atelier-solve-success">
                            <div className="solve-success-kicker">Correct</div>
                            <h2 className="solve-success-title">Slice reconstructed</h2>
                            <p className="solve-success-slice">{solveResult.sliceLabel}</p>
                            <p className="solve-success-progress">
                                {gameState.isBootcampMode ? 'Bootcamp' : 'Mystery'}{' '}
                                {solveResult.pctBefore}% →{' '}
                                <strong>{solveResult.pctAfter}%</strong>
                            </p>
                            <ul className="solve-success-rewards">
                                {solveResult.rewards.map((r) => (
                                    <li key={r.label}>
                                        <span>{r.label}</span>
                                        <strong>{r.value}</strong>
                                    </li>
                                ))}
                            </ul>
                            <p className="solve-success-hint">
                                {gameState.isBootcampMode
                                    ? 'Nice — that plane restores voxels on the practice relic. Open the next workbench when ready.'
                                    : 'Hologram updated — next slice ready'}
                            </p>
                            <button
                                type="button"
                                className="atelier-btn-primary"
                                style={{ width: '100%', marginTop: 12, justifyContent: 'center' }}
                                onClick={onClose}
                            >
                                Continue
                            </button>
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <div className="modal-backdrop atelier-modal-backdrop" onClick={onClose}>
                <div
                    className={`modal-content atelier-puzzle-modal${
                        isFocusPlay ? ' is-focus-play' : ''
                    }${isFocusPlay && phoneLayout ? ' is-phone-play' : ''}${
                        isFocusPlay && !phoneLayout ? ' is-desktop-play' : ''
                    }${isFocusPlay && roomyChrome ? ' is-roomy' : ''}${
                        isFocusPlay && immersive ? ' is-immersive' : ''
                    }`}
                    onClick={(e) => e.stopPropagation()}
                >
                    <header className="atelier-puzzle-header atelier-puzzle-header--compact">
                        <div className="atelier-puzzle-title-block">
                            <h2 className="atelier-puzzle-title">{sliceLabel}</h2>
                            <div className="atelier-puzzle-status">
                                {lockClock && !lockClock.expired && (
                                    <span
                                        className={`atelier-lock-timer${
                                            lockClock.critical
                                                ? ' is-critical'
                                                : lockClock.urgent
                                                  ? ' is-urgent'
                                                  : ''
                                        }`}
                                        title="Time left on this claim"
                                        aria-label={`Claim timer ${lockClock.label} remaining`}
                                    >
                                        ⏱ {lockClock.label}
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className="atelier-puzzle-tools-meta">
                            {roomyChrome && tile.status === 'CLAIMED' && isOwner && (
                                <div className="atelier-header-extras">
                                    <span className="atelier-keys atelier-keys--compact">
                                        <IconKey className="atelier-inline-ico" /> {energy.keys}/
                                        {ENERGY_MAX}
                                    </span>
                                    <span
                                        className={`atelier-status-pill${
                                            tile.status === 'CLAIMED' ? ' is-claimed' : ''
                                        }${tile.status === 'SOLVED' ? ' is-solved' : ''}`}
                                    >
                                        {tile.status}
                                    </span>
                                    {!isSubmitting && (
                                        <button
                                            type="button"
                                            className="atelier-chip-btn"
                                            onClick={handleRelease}
                                        >
                                            Give up
                                        </button>
                                    )}
                                </div>
                            )}
                            {tile.status === 'CLAIMED' && isOwner && canFsToggle && (
                                <button
                                    type="button"
                                    className={`atelier-chip-btn${fsActive ? ' is-active' : ''}`}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        const dense = immersive || gridN >= 16;
                                        if (isTelegramFullscreen()) {
                                            setPuzzleFullscreenPref(false);
                                            exitPuzzleFullscreen();
                                            window.setTimeout(() => {
                                                applyTelegramSafeAreaVars();
                                                setFsActive(false);
                                            }, 80);
                                        } else {
                                            setPuzzleFullscreenPref(true);
                                            const before = estimatePuzzleStageBudget({
                                                fullscreen: false,
                                                denseChrome: dense,
                                            });
                                            requestPuzzleFullscreen({ force: true, denseChrome: dense });
                                            window.setTimeout(() => {
                                                applyTelegramSafeAreaVars();
                                                verifyPuzzleFullscreenHelped({
                                                    beforeBudget: before,
                                                    denseChrome: dense,
                                                });
                                                setFsActive(isTelegramFullscreen());
                                            }, 450);
                                        }
                                    }}
                                    aria-pressed={fsActive}
                                    title={
                                        fsActive
                                            ? 'Exit Telegram fullscreen'
                                            : 'Expand to Telegram fullscreen'
                                    }
                                >
                                    {fsActive ? 'Exit' : 'Expand'}
                                </button>
                            )}
                            {tile.status === 'CLAIMED' && isOwner && !showGuidedCoach && (
                                <button
                                    type="button"
                                    className={`atelier-chip-btn${showAssists ? ' is-active' : ''}`}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setShowMore(false);
                                        setAssistSheet(null);
                                        setShowAssists((v) => !v);
                                    }}
                                    aria-expanded={showAssists}
                                >
                                    Tools
                                </button>
                            )}
                            <button
                                type="button"
                                className={`atelier-chip-btn${showMore ? ' is-active' : ''}`}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setShowAssists(false);
                                    setShowMore((v) => !v);
                                }}
                                aria-expanded={showMore}
                            >
                                More
                            </button>
                            <button
                                type="button"
                                className="atelier-chip-btn"
                                onClick={onClose}
                                aria-label="Minimize puzzle"
                            >
                                Hub
                            </button>
                        </div>
                    </header>

                    {showMore && (
                        <div className="atelier-puzzle-drawer" role="region" aria-label="More options">
                            {!roomyChrome && (
                                <div className="atelier-puzzle-drawer-row">
                                    <span className="atelier-keys atelier-keys--compact">
                                        <IconKey className="atelier-inline-ico" /> {energy.keys}/
                                        {ENERGY_MAX}
                                    </span>
                                    <span
                                        className={`atelier-status-pill${
                                            tile.status === 'CLAIMED' ? ' is-claimed' : ''
                                        }${tile.status === 'SOLVED' ? ' is-solved' : ''}`}
                                    >
                                        {tile.status}
                                    </span>
                                </div>
                            )}
                            {canUseDebugTools() && tile.status === 'CLAIMED' && isOwner && (
                                <button
                                    type="button"
                                    className="atelier-chip-btn"
                                    onClick={handleDebugSolve}
                                >
                                    DEBUG solve
                                </button>
                            )}
                            {canUseDebugTools() && (
                                <button
                                    type="button"
                                    className="atelier-chip-btn"
                                    onClick={() => setShowSolution(!showSolution)}
                                >
                                    {showSolution ? 'HIDE peek' : 'PEEK'}
                                </button>
                            )}
                            {tile.status === 'CLAIMED' && isOwner && !isSubmitting && !roomyChrome && (
                                <button
                                    type="button"
                                    className="atelier-btn-secondary"
                                    onClick={handleRelease}
                                >
                                    Give up slice
                                </button>
                            )}
                        </div>
                    )}

                    {localFeedback && (
                        <div
                            className={`atelier-puzzle-flash${
                                localFeedback.isError ? ' is-error' : ' is-ok'
                            }`}
                        >
                            {localFeedback.msg}
                        </div>
                    )}

                    <div className="atelier-puzzle-body">
                        {isSubmitting ? (
                            <div className="atelier-puzzle-loading">Transmitting carve…</div>
                        ) : (
                            <>
                                {tile.status === 'OPEN' && (
                                    <>
                                        {isBootcamp && (
                                            <div className="atelier-bootcamp-open-tip">
                                                <strong>
                                                    {tutorialMeta?.title || 'Practice slice'}
                                                </strong>
                                                <p>
                                                    {tutorialMeta?.blurb ||
                                                        'This is a free nonogram practice board. Numbers tell you how many clay cells belong in each row and column.'}
                                                </p>
                                            </div>
                                        )}
                                        <button
                                            type="button"
                                            className="atelier-btn-primary"
                                            style={{ width: '100%', justifyContent: 'center' }}
                                            onClick={handleClaim}
                                        >
                                            {isBootcamp ? (
                                                'Start free practice'
                                            ) : (
                                                <>
                                                    Play
                                                    <span
                                                        className="atelier-btn-cost"
                                                        aria-label="Costs 1 key"
                                                    >
                                                        <IconKey className="atelier-btn-cost-ico" />
                                                        <span>−1</span>
                                                    </span>
                                                </>
                                            )}
                                        </button>
                                    </>
                                )}

                                {tile.status === 'CLAIMED' && isOwner && (
                                    <>
                                        {showGuidedCoach && guidedStep && (
                                            <TutorialCoach
                                                step={coachStep + 1}
                                                total={GUIDED_BOARD_STEPS.length}
                                                title={guidedStep.title}
                                                body={guidedStep.body}
                                                hint={tutorialMeta?.title}
                                                highlight={guidedStep.highlight}
                                                primaryLabel={guidedStep.primaryLabel}
                                                secondaryLabel="Skip guided hints"
                                                tertiaryLabel={guidedStep.tertiaryLabel}
                                                onSecondary={() => {
                                                    setCoachDismissed(true);
                                                    setHighlights([]);
                                                }}
                                                onTertiary={() => {
                                                    if (!guidedSolution || !guidedStep.tertiaryLabel) {
                                                        return;
                                                    }
                                                    const painted = paintGuidedStep(
                                                        gridState,
                                                        guidedSolution,
                                                        guidedStep.id
                                                    );
                                                    setGridState(painted);
                                                    setGridEpoch((n) => n + 1);
                                                    setHighlights([]);
                                                    setCoachStep((s) =>
                                                        Math.min(s + 1, GUIDED_BOARD_STEPS.length - 1)
                                                    );
                                                }}
                                                onPrimary={() => {
                                                    if (
                                                        (guidedStep.id === 'draw' ||
                                                            guidedStep.id === 'mark') &&
                                                        guidedSolution &&
                                                        !guidedStepComplete(
                                                            guidedSolution,
                                                            guidedStep.id,
                                                            gridState
                                                        )
                                                    ) {
                                                        handleFeedback(
                                                            guidedStep.id === 'draw'
                                                                ? 'Paint every orange-glowing cell with DRAW first'
                                                                : 'Put an X on every pale-glowing cell with MARK first',
                                                            true
                                                        );
                                                        return;
                                                    }
                                                    if (coachStep >= GUIDED_BOARD_STEPS.length - 1) {
                                                        setCoachDismissed(true);
                                                        setHighlights([]);
                                                    } else {
                                                        setCoachStep((s) => s + 1);
                                                    }
                                                }}
                                            />
                                        )}
                                        {isBootcamp && !showGuidedCoach && tutorialMeta && (
                                            <p className="atelier-bootcamp-inline-tip">
                                                {tutorialMeta.blurb}
                                            </p>
                                        )}
                                        {showAssists && !showGuidedCoach && (
                                            <div
                                                className="atelier-assist-tray atelier-assist-tray--drawer"
                                                role="group"
                                                aria-label="Atelier assists"
                                            >
                                            {[
                                                ASSIST.VOID_STAMP,
                                                ASSIST.ERROR_DUST,
                                                ASSIST.CHALK_TIP,
                                                ASSIST.LOCK_OIL,
                                                ASSIST.SPARE_KEY,
                                            ].map((id) => (
                                                <button
                                                    key={id}
                                                    type="button"
                                                    className="atelier-assist-btn"
                                                    aria-label={`${ASSIST_META[id].name}. ${ASSIST_META[id].blurb}. ${assists[id] || 0} left`}
                                                    onClick={() => setAssistSheet(id)}
                                                >
                                                    {ASSIST_META[id].name}
                                                    <span className="atelier-assist-count">
                                                        {assists[id] || 0}
                                                    </span>
                                                </button>
                                            ))}
                                            </div>
                                        )}
                                        {assistSheet && ASSIST_META[assistSheet] && (
                                            <div
                                                className="atelier-assist-sheet"
                                                role="dialog"
                                                aria-label={ASSIST_META[assistSheet].name}
                                            >
                                                <div className="atelier-assist-sheet-head">
                                                    <div>
                                                        <div className="atelier-assist-sheet-name">
                                                            {ASSIST_META[assistSheet].name}
                                                        </div>
                                                        <div className="atelier-assist-sheet-qty">
                                                            {assists[assistSheet] || 0} in satchel
                                                        </div>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        className="atelier-chip-btn"
                                                        onClick={() => setAssistSheet(null)}
                                                    >
                                                        Close
                                                    </button>
                                                </div>
                                                <p className="atelier-assist-sheet-blurb">
                                                    {assistSheet === ASSIST.LOCK_OIL && lockClock
                                                        ? `${ASSIST_META[assistSheet].blurb} Right now you have ${lockClock.label} left — after oil: about ${formatLockCountdown((tile.lockExpiry || Date.now()) + 5 * 60 * 1000).label}.`
                                                        : ASSIST_META[assistSheet].blurb}
                                                </p>
                                                <div className="atelier-assist-sheet-actions">
                                                    <button
                                                        type="button"
                                                        className="atelier-btn-secondary"
                                                        onClick={() => setAssistSheet(null)}
                                                    >
                                                        Not now
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="atelier-btn-primary"
                                                        disabled={!assists[assistSheet]}
                                                        onClick={() => handleAssist(assistSheet)}
                                                    >
                                                        Use tool
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                        <PuzzleGrid
                                            key={`${tile.id}-${gridEpoch}`}
                                            clues={puzzleData.clues}
                                            initialGrid={gridState}
                                            peekGrid={
                                                showSolution ? puzzleData.solution : null
                                            }
                                            isInteractive={!showSolution}
                                            highlights={highlights}
                                            compactChrome
                                            immersiveLandscape={immersive}
                                            desktopLayout={!phoneLayout}
                                            paintMode={
                                                showGuidedCoach ? guidedStep?.paintMode || null : null
                                            }
                                            onGridChange={(g) => {
                                                if (!showGuidedCoach) setHighlights([]);
                                                setGridState(g);
                                            }}
                                        />
                                    </>
                                )}

                                {tile.status === 'CLAIMED' && !isOwner && (
                                    <div className="atelier-puzzle-locked">
                                        Locked by {tile.claimedBy}
                                    </div>
                                )}

                                {tile.status === 'SOLVED' && (
                                    <div className="atelier-puzzle-solved">
                                        Restored by {tile.completedBy}
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    {tile.status === 'CLAIMED' && isOwner && !isSubmitting && (
                        <footer
                            className={`atelier-puzzle-footer atelier-puzzle-footer--focus${
                                immersive ? ' is-floating-check' : ''
                            }`}
                        >
                            <button
                                type="button"
                                className="atelier-btn-primary"
                                onClick={() => handleSolve()}
                                disabled={solvingRef.current}
                            >
                                {gridFullyFilled ? 'Check again' : 'Check slice'}
                            </button>
                            {!immersive && (
                                <span className="atelier-autosave">Draft auto-saved</span>
                            )}
                        </footer>
                    )}
                    {!(tile.status === 'CLAIMED' && isOwner) && (
                        <footer className="atelier-puzzle-footer">
                            <button type="button" className="atelier-minimize" onClick={onClose}>
                                <span className="atelier-minimize-label">Close</span>
                            </button>
                        </footer>
                    )}
                </div>
            </div>
        );
    } catch (err) {
        console.error('[TileDetailsModal] Render Error:', err);
        return (
            <div className="atelier-render-crash">
                RENDER CRASH: {err.message}
            </div>
        );
    }
};

export default TileDetailsModal;
